import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { useMyContext } from '../../../components/Provider/CommonProvider';
import AdminCustomButton from '../../../components/CommonComponents/AdminCustomButton';
import { IoClose } from 'react-icons/io5';
import MessageLabel from '../../../components/CommonComponents/MessageLabel';
import AdminSwitch from '../../../components/CommonComponents/AdminSwitch';
import MessageSubHeading from '../../../components/CommonComponents/MessageSubHeading';
import CameraRetroIcon from "@rsuite/icons/legacy/CameraRetro";
import "../../Site/Settings/ThemeSettings.css"; // Import styles from ThemeSettings

const SubFooterLogoModal = ({ show, handleClose }) => {
    const { fetchRequest, globalImageUplaodAPI } = useMyContext();
    const imageApi = globalImageUplaodAPI();
    const baseURL = process.env.REACT_APP_Baseurl;

    const [loading, setLoading] = useState(false);
    const [fileLoading, setFileLoading] = useState(false);
    const [subFooterLogoActive, setSubFooterLogoActive] = useState(0);
    const [lightModeLogo, setLightModeLogo] = useState(null);
    const [darkModeLogo, setDarkModeLogo] = useState(null);
    const [selectedLightFile, setSelectedLightFile] = useState(null);
    const [selectedDarkFile, setSelectedDarkFile] = useState(null);

    const [lightError, setLightError] = useState("");
    const [darkError, setDarkError] = useState("");

    const lightInputRef = useRef(null);
    const darkInputRef = useRef(null);

    const fetchSubFooterMenu = async () => {
        try {
            const response = await fetchRequest({
                method: 'GET',
                url: `${baseURL}/admin/settings/sub-footer-menu`,
                params: {},
                data: {},
            });
            if (response?.status) {
                const data = response.footer_data || {};
                setSubFooterLogoActive(data.sub_footer_logo_active || 0);

                // Assuming the API returns full URLs now based on previous fixes
                setLightModeLogo(data.sub_footer_logo_light || null);
                setDarkModeLogo(data.sub_footer_logo_dark || null);
            }
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        if (show) {
            fetchSubFooterMenu();
            setSelectedLightFile(null);
            setSelectedDarkFile(null);
            setLightError("");
            setDarkError("");
        }
    }, [show]);

    const handleFileChange = (event, type, setError) => {
        const file = event.target.files[0];
        setError("");

        if (!file) return;

        if (!file.type.startsWith("image/")) {
            setError("Only image files are allowed.");
            event.target.value = null;
            return;
        }

        if (type === 'light') {
            setSelectedLightFile(file);
        } else {
            setSelectedDarkFile(file);
        }
    };

    const handleDelete = (type) => {
        if (type === 'light') {
            setSelectedLightFile(null);
            if (lightInputRef.current) lightInputRef.current.value = null;
        } else {
            setSelectedDarkFile(null);
            if (darkInputRef.current) darkInputRef.current.value = null;
        }
    };

    const handleUpdate = async () => {
        setLoading(true);
        try {
            const response = await fetchRequest({
                method: 'POST',
                url: `${baseURL}/admin/settings/sub-footer-menu`,
                data: { sub_footer_logo_active: subFooterLogoActive },
            });

            if (response?.status) {
                if (selectedLightFile || selectedDarkFile) {
                    await uploadImages();
                } else {
                    toast.success("Sub Footer Logo settings updated successfully");
                    handleClose();
                }
            } else {
                toast.error(response.message);
            }
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Error updating settings');
        } finally {
            setLoading(false);
        }
    };

    const uploadImages = async () => {
        setFileLoading(true);
        const imageFormData = new FormData();
        const modelName = "AdminSetting";
        const id = 1;

        imageFormData.append("id", id);
        imageFormData.append("modelName", modelName);

        const imageFields = [];
        if (selectedLightFile) {
            imageFormData.append("sub_footer_logo_light", selectedLightFile);
            imageFields.push("sub_footer_logo_light");
        }
        if (selectedDarkFile) {
            imageFormData.append("sub_footer_logo_dark", selectedDarkFile);
            imageFields.push("sub_footer_logo_dark");
        }

        if (imageFields.length > 0) {
            imageFields.forEach((field) => {
                imageFormData.append("imageFields[]", field);
            });

            try {
                const response = await fetchRequest({
                    method: 'POST',
                    url: imageApi,
                    data: imageFormData,
                });
                if (response) {
                    toast.success("Sub Footer Logo settings updated successfully");
                    handleClose();
                }

            } catch (error) {
                console.error("Image upload error:", error);
                toast.error("Error uploading images");
            }
        }
        setFileLoading(false);
    };


    return (
        <div className={`modal packageModel fade ${show ? 'd-block show' : ''}`} tabIndex="-1" role="dialog" aria-hidden="true">
            <div className="modal-dialog modal-lg theme-bg-color rounded-3" role="document">
                <div className="modal-content theme-bg-color">
                    <div className="modal-header theme-border-bottom-secondary">
                        <h5 className="modal-title theme-text-color">Sub Footer Logo</h5>
                        <button type="button" className="bg-transparent theme-text-color border-0" onClick={handleClose}>
                            <IoClose size={25} fill="currentColor" />
                        </button>
                    </div>
                    <div className="modal-body">
                        {/* Toggle Switch */}
                        <div className="row mx-0 mb-3">
                            <div className="col-9 ps-0">
                                <MessageLabel text="Show Logo in Sub Footer" />
                            </div>
                            <div className="col-3 text-end pe-0">
                                <AdminSwitch
                                    name="sub_footer_logo_active"
                                    checked={subFooterLogoActive == 1}
                                    onChange={(e) => setSubFooterLogoActive(e.target.checked ? 1 : 0)}
                                    id="sub_footer_logo_active_toggle"
                                />
                            </div>
                        </div>

                        {/* Dark Mode Logo (to match screenshot order) */}
                        <MessageLabel text="Dark Mode" />
                        <MessageSubHeading className="colorText" text="Select the logo image" />
                        {darkModeLogo && !selectedDarkFile && (
                            <img src={darkModeLogo} alt="Current Dark Mode Logo" className="thumbnails-images-theme-setting mb-3" style={{ maxHeight: '40px', objectFit: 'contain' }} />
                        )}

                        <div className="mx-0 row mb-4">
                            <div className="col-6 ps-0">
                                <div
                                    className={`imagedrop theme-border-color ${darkError ? "border-danger dotted-border-border-border" : ""}`}
                                    onClick={() => darkInputRef.current.click()}
                                >
                                    <input
                                        type="file"
                                        ref={darkInputRef}
                                        onChange={(e) => handleFileChange(e, 'dark', setDarkError)}
                                        accept="image/*"
                                        style={{ display: "none" }}
                                    />
                                    <label className="theme-text-color">
                                        <CameraRetroIcon className="theme-text-color" />
                                    </label>
                                </div>
                                {darkError && <p className="text-danger-dotted-red mt-1">{darkError}</p>}
                            </div>

                            <div className="col-6">
                                {selectedDarkFile && (
                                    <div className="imagedropcopy theme-border-color text-end">
                                        <div className="row m-0">
                                            <div className="col-10 p-0">
                                                <img src={URL.createObjectURL(selectedDarkFile)} alt="Uploaded" className="thumbnails-upload-images object-fit-contain" />
                                            </div>
                                            <div className="col-2 p-0" style={{ alignSelf: "center" }}>
                                                <button onClick={() => handleDelete('dark')} type="button" className="theme-text-color bg-transparent border-0"><IoClose className="custom-close-btn" /></button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>


                        {/* Light Mode Logo */}
                        <MessageLabel text="Light Mode" />
                        <MessageSubHeading className="colorText" text="Select the logo image" />
                        {lightModeLogo && !selectedLightFile && (
                            <img src={lightModeLogo} alt="Current Light Mode Logo" className="thumbnails-images-theme-setting mb-3" style={{ maxHeight: '40px', objectFit: 'contain' }} />
                        )}

                        <div className="mx-0 row mb-3">
                            <div className="col-6 ps-0">
                                <div
                                    className={`imagedrop theme-border-color ${lightError ? "border-danger dotted-border-border-border" : ""}`}
                                    onClick={() => lightInputRef.current.click()}
                                >
                                    <input
                                        type="file"
                                        ref={lightInputRef}
                                        onChange={(e) => handleFileChange(e, 'light', setLightError)}
                                        accept="image/*"
                                        style={{ display: "none" }}
                                    />
                                    <label className="theme-text-color">
                                        <CameraRetroIcon className="theme-text-color" />
                                    </label>
                                </div>
                                {lightError && <p className="text-danger-dotted-red mt-1">{lightError}</p>}
                            </div>

                            <div className="col-6">
                                {selectedLightFile && (
                                    <div className="imagedropcopy theme-border-color text-end">
                                        <div className="row m-0">
                                            <div className="col-10 p-0">
                                                <img src={URL.createObjectURL(selectedLightFile)} alt="Uploaded" className="thumbnails-upload-images object-fit-contain" />
                                            </div>
                                            <div className="col-2 p-0" style={{ alignSelf: "center" }}>
                                                <button onClick={() => handleDelete('light')} type="button" className="theme-text-color bg-transparent border-0"><IoClose className="custom-close-btn" /></button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer border-0">
                        <button type="button" className="bg-transparent btn theme-text-color m-0 sliderBtnText" onClick={handleClose}>Close</button>
                        <AdminCustomButton saveMethod={handleUpdate} saveText="Update" loading={loading || fileLoading} className="m-0" />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SubFooterLogoModal;
